import { HistoryOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import {
  CaptureWorkflowPendingReviewIcon,
  CaptureWorkflowPostedIcon,
  CaptureWorkflowReadyToPostIcon,
} from 'components/Entity/capture-workflow-metrics';
import {
  DrawerDateRangeFilter,
  useDrawerDateRangeFilter,
} from 'components/Custom/drawer-date-range-filter';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DateOptions } from 'store/api/enums';
import { turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { fetchReceiptStatisticsEntityRequest } from 'store/organisation/actions';
import {
  ReceiptStatisticsTotals,
} from 'store/organisation/models';
import {
  getReceiptEntityStats,
  getReceiptStatisticsEntityByKey,
  getReceiptStatisticsEntityLoading,
} from 'store/organisation/selectors';
import { ApiDateRange, getApiDateRangeForDateOption } from 'utils/date-option-range';
import {
  entityStatsToTotals,
  receiptStatisticsEntityCacheKey,
} from 'utils/receipt-statistics';
import { ENTITY_TOOLBAR_MUTED_ICON_COLOR } from './transactions-tab/constants';
import {
  EntityTransactionsToolbarIconButton,
  entityTransactionsToolbarButtonStyle,
} from './transactions-tab/styles';

const DEFAULT_CAPTURE_STATISTICS_DATE_OPTION = DateOptions.THIS_YEAR;

interface Props {
  connectionId: string;
  organisationId?: string;
  variant?: 'icon' | 'button';
  buttonLabel?: string;
  disabled?: boolean;
}

type StatRow = {
  labelKey: string;
  value: number;
};

type SummaryCard = {
  titleKey: string;
  primary: number;
  secondary?: number;
  secondaryLabelKey?: string;
  tone: 'pending' | 'reviewed' | 'posted';
  icon: React.ReactNode;
};

const CaptureStatisticsSummary: React.FC<{
  stats: ReceiptStatisticsTotals;
}> = ({ stats }) => {
  const { t } = useTranslation();

  const cards: SummaryCard[] = [
    {
      titleKey: 'organisation.transactions-view-tab-pending-review',
      primary: stats.filesPendingReview,
      secondary: stats.transactionsPendingReview,
      secondaryLabelKey: 'exceptions.transactions-lowercase',
      tone: 'pending',
      icon: <CaptureWorkflowPendingReviewIcon />,
    },
    {
      titleKey: 'organisation.file-badge-reviewed',
      primary: stats.filesReviewed,
      secondary: stats.reviewedTransactions,
      secondaryLabelKey: 'exceptions.transactions-lowercase',
      tone: 'reviewed',
      icon: <CaptureWorkflowReadyToPostIcon />,
    },
    {
      titleKey: 'organisation.transactions-view-tab-posted',
      primary: stats.postedFiles,
      secondary: stats.postedThisMonth,
      secondaryLabelKey:
        'organisation.capture-statistics-card-posted-subtitle',
      tone: 'posted',
      icon: <CaptureWorkflowPostedIcon />,
    },
  ];

  return (
    <div className="capture-stat-history__summary">
      {cards.map((card) => (
        <div
          key={card.titleKey}
          className={`capture-stat-history__summary-card capture-stat-history__summary-card--${card.tone}`}
        >
          <div className="capture-stat-history__summary-heading">
            {card.icon}
            <span className="capture-stat-history__summary-label">
              {t(card.titleKey)}
            </span>
          </div>
          <span className="capture-stat-history__summary-value">
            {card.primary}
          </span>
          {card.secondary != null && card.secondaryLabelKey ? (
            <span className="capture-stat-history__summary-meta">
              {card.titleKey === 'organisation.transactions-view-tab-posted'
                ? t(card.secondaryLabelKey, { count: card.secondary })
                : `${card.secondary} ${t(card.secondaryLabelKey)}`}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const CaptureStatisticsSection: React.FC<{
  title: string;
  rows: StatRow[];
}> = ({ title, rows }) => {
  const { t } = useTranslation();

  return (
    <section className="capture-stat-history__section-card">
      <h4 className="capture-stat-history__section-title">{title}</h4>
      <div className="capture-stat-history__metric-grid">
        {rows.map((row) => (
          <div className="capture-stat-history__metric" key={row.labelKey}>
            <span className="capture-stat-history__metric-label">
              {t(row.labelKey)}
            </span>
            <span className="capture-stat-history__metric-value">
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

const CaptureStatisticsContent: React.FC<{
  connectionId: string;
  organisationId?: string;
}> = ({ connectionId, organisationId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const applyDateRange = React.useCallback(
    (nextRange: ApiDateRange) => {
      dispatch(fetchReceiptStatisticsEntityRequest(connectionId, nextRange));
    },
    [connectionId, dispatch]
  );

  const { appliedRange, filterProps } = useDrawerDateRangeFilter({
    defaultOption: DEFAULT_CAPTURE_STATISTICS_DATE_OPTION,
    onApply: applyDateRange,
  });

  const cachedEntityStats = useSelector(
    getReceiptEntityStats(connectionId, organisationId ?? connectionId)
  );

  const hasDateFilter = Boolean(appliedRange.from || appliedRange.to);
  const cacheKey = receiptStatisticsEntityCacheKey(connectionId, appliedRange);
  const filteredStats = useSelector(getReceiptStatisticsEntityByKey(cacheKey));
  const entityLoading = useSelector(getReceiptStatisticsEntityLoading);

  const displayStats: ReceiptStatisticsTotals | undefined = useMemo(() => {
    if (hasDateFilter) return filteredStats;
    if (cachedEntityStats) return entityStatsToTotals(cachedEntityStats);
    return filteredStats;
  }, [hasDateFilter, filteredStats, cachedEntityStats]);

  useEffect(() => {
    dispatch(
      fetchReceiptStatisticsEntityRequest(
        connectionId,
        getApiDateRangeForDateOption(DEFAULT_CAPTURE_STATISTICS_DATE_OPTION)
      )
    );
  }, [connectionId, dispatch]);

  const showLoading =
    entityLoading && !displayStats && (hasDateFilter || !cachedEntityStats);

  return (
    <div className="capture-stat-history drawer-panel-root">
      <DrawerDateRangeFilter {...filterProps} />

      <div className="capture-stat-history__body">
        {showLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : !displayStats ? (
          <div className="capture-stat-history__empty">
            {t('organisation.capture-statistics-no-data')}
          </div>
        ) : (
          <>
            <CaptureStatisticsSummary stats={displayStats} />

            <div className="capture-stat-history__sections">
              <CaptureStatisticsSection
                title={t('organisation.capture-statistics-history-uploads-section')}
                rows={[
                  {
                    labelKey:
                      'organisation.capture-statistics-field-uploaded-files-total',
                    value: displayStats.uploadedFilesTotal,
                  },
                  {
                    labelKey:
                      'organisation.capture-statistics-field-uploaded-this-month',
                    value: displayStats.uploadedThisMonth,
                  },
                  {
                    labelKey:
                      'organisation.capture-statistics-field-uploaded-manually',
                    value: displayStats.uploadedManually,
                  },
                  {
                    labelKey:
                      'organisation.capture-statistics-field-uploaded-from-email',
                    value: displayStats.uploadedFromEmail,
                  },
                  {
                    labelKey:
                      'organisation.capture-statistics-field-uploaded-from-google-drive',
                    value: displayStats.uploadedFromGoogleDrive,
                  },
                  {
                    labelKey:
                      'organisation.capture-statistics-field-uploaded-from-onedrive',
                    value: displayStats.uploadedFromOneDrive,
                  },
                ]}
              />

              <CaptureStatisticsSection
                title={t('dashboard.other')}
                rows={[
                  {
                    labelKey:
                      'organisation.capture-statistics-field-files-with-errors',
                    value: displayStats.filesWithErrors,
                  },
                  {
                    labelKey:
                      'organisation.capture-statistics-field-deleted-files',
                    value: displayStats.deletedFiles,
                  },
                  {
                    labelKey:
                      'organisation.capture-statistics-field-active-files',
                    value: displayStats.activeFiles,
                  },
                ]}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CaptureStatisticsHistory: React.FC<Props> = ({
  connectionId,
  organisationId,
  variant = 'button',
  buttonLabel,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const drawerData: DrawerData = {
      title: t('organisation.capture-statistics-history-title'),
      subtitle: t('organisation.capture-statistics-history-subtitle'),
      type: DrawerType.CAPTURE_STATISTICS,
      wide: true,
      component: (
        <CaptureStatisticsContent
          connectionId={connectionId}
          organisationId={organisationId}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
    setOpen(false);
  }, [open, connectionId, organisationId, dispatch, t]);

  const label =
    buttonLabel ?? t('users.history');

  const openHistory = () => {
    if (disabled) return;
    setOpen(true);
  };

  if (variant === 'button') {
    return (
      <EntityTransactionsToolbarIconButton
        type="default"
        icon={
          <HistoryOutlined
            style={{
              fontSize: 14,
              color: ENTITY_TOOLBAR_MUTED_ICON_COLOR,
            }}
          />
        }
        style={entityTransactionsToolbarButtonStyle(false)}
        onClick={openHistory}
        disabled={disabled}
      >
        {label}
      </EntityTransactionsToolbarIconButton>
    );
  }

  return (
    <EntityTransactionsToolbarIconButton
      type="default"
      icon={
        <HistoryOutlined
          style={{
            fontSize: 14,
            color: ENTITY_TOOLBAR_MUTED_ICON_COLOR,
          }}
        />
      }
      style={entityTransactionsToolbarButtonStyle(false)}
      onClick={openHistory}
      disabled={disabled}
      aria-label={label}
    />
  );
};

export default CaptureStatisticsHistory;
